import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CustomAutocomplete from 'components/shared/CustomAutocomplete/CustomAutocomplete';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomDragnDrop from 'components/shared/CustomDragnDrop/CustomDragnDrop';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import zeplinColor from 'theme/Color';
import { LeadAttachments } from '../CustomViewLead/components/LeadAttachement';

const MenuProps = {
  PaperProps: {
    sx: { borderRadius: '14px' },
  },
};

export type LeadDetailsFormProps = {
  formValues: Record<string, string>;
  selectedItems: {
    assignTo: { Name: string; Id: string }[];
    address: { Name: string; Id: string }[];
  };
  dropdownOptions: {
    salutation: string[];
    leadStatus: string[];
    level: string[];
    college: string[];
    sport: string[];
    position: string[];
    class: string[];
    draftEligible: string[];
    redshirt: string[];
    draftProjection: string[];
    espnRanking: string[];
    sports247Rating: string[];
  };
  assigneeDropdown: { Name: string; Id: string }[];
  addressOptions: { Name: string; Id: string }[];
  loadingAddress: boolean;
  avatarPreview: string | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleValueChange: (key: string) => (event: any) => void;
  handleSelectChange: (key: string) => (event: any) => void;
  handleAssignSelection: (value: any) => void;
  handleAddressSelection: (value: any) => void;
  handleAddressSearch: (e: React.SyntheticEvent, value: string) => void;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAttachmentDrop: (files: File[] | FileList | any) => void;
  attachmentFiles?: (File | string)[];
  onAttachmentDelete?: (index: number) => void;
  openFileSelection: () => void;
  clearSelection: () => void;
  leadId?: string;
};

const LeadDetailsForm = ({
  formValues,
  selectedItems,
  dropdownOptions,
  assigneeDropdown,
  addressOptions,
  loadingAddress,
  avatarPreview,
  fileInputRef,
  handleValueChange,
  handleSelectChange,
  handleAssignSelection,
  handleAddressSelection,
  handleAddressSearch,
  handleFileChange,
  handleAttachmentDrop,
  attachmentFiles = [],
  onAttachmentDelete,
  openFileSelection,
  clearSelection,
  leadId,
}: LeadDetailsFormProps) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '24px' }}>
      <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
        Lead Information
      </Typography>

      {/* Profile Image */}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '80px',
            height: '80px',
            borderRadius: '22px',
            border: !avatarPreview ? `1px dashed ${zeplinColor.Neutral9}` : '',
            overflow: 'hidden',
          }}
        >
          <img
            src={avatarPreview ?? '/assets/account-name.svg'}
            alt='profile icon'
            width={avatarPreview ? 80 : 35}
            height={avatarPreview ? 80 : 35}
            style={{ objectFit: 'cover' }}
          />
        </Box>
        <Box
          sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Typography variant='body2' sx={{ color: zeplinColor.BasicBlack }}>
              Profile photo
            </Typography>
            <Typography variant='label1' sx={{ color: zeplinColor.Neutral8 }}>
              JPG or PNG (max 2 MB)
            </Typography>
          </Box>
          <Box>
            <CustomButton
              disabled={false}
              onClick={openFileSelection}
              variant='contained'
              color={'primary'}
              buttonVariant='buttonSmall'
              style={{
                borderRadius: { xs: '10px', xl: '14px' },
                padding: '6px 12px',
                '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
              }}
              textStyle={{ color: zeplinColor.Neutral1 }}
              text={avatarPreview ? 'Change Photo' : 'Add Photo'}
            />
            <input
              type='file'
              multiple
              accept='.jpeg, .jpg, .png, image/jpeg, image/png'
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            {avatarPreview && (
              <CustomButton
                disabled={false}
                variant='contained'
                buttonVariant='buttonSmall'
                onClick={clearSelection}
                text={'Delete Photo'}
                textStyle={{ color: zeplinColor.Error }}
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: { xs: '10px', xl: '14px' },
                  marginLeft: '8px',
                  padding: '6px 12px',
                  '&.Mui-focusVisible, &:hover': { backgroundColor: zeplinColor.Neutral3 },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Salutation + Name */}
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <FormControl sx={{ width: '100%', flex: 1 }}>
          <InputLabel
            id='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
          >
            Salutation
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            value={formValues.salutation || ''}
            onChange={handleSelectChange('salutation')}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Salutation'
              />
            }
            renderValue={(selected) =>
              `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`
            }
            MenuProps={MenuProps}
            IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
          >
            {dropdownOptions.salutation.map((name) => (
              <MenuItem key={name} value={name}>
                <Radio
                  checked={formValues.salutation === name}
                  sx={{
                    color: zeplinColor.BasicBlack,
                    '&.Mui-checked': { color: zeplinColor.BasicBlack },
                  }}
                />
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <CustomTextField
          style={{ flex: 3 }}
          name='Name'
          text={formValues.name || ''}
          form
          label='Name'
          type='text'
          onChange={handleValueChange('name')}
          required
        />
      </Box>

      {/* Lead Status */}
      <Box sx={{ display: 'flex', gap: '24px' }}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel
            required
            id='lead-status-label'
            size={isXs ? 'small' : 'medium'}
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? 14 : 16,
              '& .MuiFormLabel-asterisk': { color: zeplinColor.Error },
            }}
          >
            Lead Status
          </InputLabel>
          <Select
            required
            labelId='lead-status-label'
            size={isXs ? 'small' : 'medium'}
            value={formValues.leadStatus || ''}
            onChange={handleSelectChange('leadStatus')}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Lead Status'
              />
            }
            renderValue={(selected) =>
              `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`
            }
            MenuProps={MenuProps}
            IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
          >
            {dropdownOptions.leadStatus.map((name) => (
              <MenuItem key={name} value={name}>
                <Radio
                  checked={formValues.leadStatus === name}
                  sx={{
                    color: zeplinColor.BasicBlack,
                    '&.Mui-checked': { color: zeplinColor.BasicBlack },
                  }}
                />
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Assign To */}
      <Box sx={{ flex: 1 }}>
        <CustomAutocomplete
          required={true}
          options={assigneeDropdown || []}
          getOptionLabel={(option: any) => option?.Name ?? ''}
          label='Assign To'
          handleDeleteItems={() => {}}
          selectedItems={selectedItems}
          handleSelection={handleAssignSelection}
          value={selectedItems.assignTo[0]}
          handleFilter={() => {}}
          placeholder='Search people'
          currentLength={assigneeDropdown.length}
          totalLength={assigneeDropdown.length}
          next={() => {}}
          multiple={false}
        />
      </Box>

      {/* Details */}
      <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
        Details
      </Typography>

      {
        [
          ['Level', 'level', 'level'],
          ['College', 'college', 'college'],
        ].map(([label, key, optionKey]) => null) /* rendered below as pairs */
      }

      <Box sx={{ display: 'flex', gap: '24px' }}>
        {(['level', 'college'] as const).map((key) => (
          <FormControl key={key} sx={{ flex: 1 }}>
            <InputLabel
              size={isXs ? 'small' : 'medium'}
              sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
            >
              {key.charAt(0).toUpperCase() + key.slice(1)}
            </InputLabel>
            <Select
              size={isXs ? 'small' : 'medium'}
              value={formValues[key] || ''}
              onChange={handleSelectChange(key)}
              input={
                <OutlinedInput
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: zeplinColor.Neutral9,
                    },
                  }}
                  label={key.charAt(0).toUpperCase() + key.slice(1)}
                />
              }
              renderValue={(s) => `${s.slice(0, 1).toLocaleUpperCase()}${s.slice(1)}`}
              MenuProps={MenuProps}
              IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
            >
              {dropdownOptions[key as keyof typeof dropdownOptions].map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={formValues[key] === name}
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': { color: zeplinColor.BasicBlack },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        {(['draftEligible', 'redshirt'] as const).map((key) => (
          <FormControl key={key} sx={{ flex: 1 }}>
            <InputLabel
              size={isXs ? 'small' : 'medium'}
              sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
            >
              {key === 'draftEligible' ? 'Draft Eligible' : 'Redshirt'}
            </InputLabel>
            <Select
              size={isXs ? 'small' : 'medium'}
              value={formValues[key] || ''}
              onChange={handleSelectChange(key)}
              input={
                <OutlinedInput
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: zeplinColor.Neutral9,
                    },
                  }}
                  label={key === 'draftEligible' ? 'Draft Eligible' : 'Redshirt'}
                />
              }
              renderValue={(s) => `${s.slice(0, 1).toLocaleUpperCase()}${s.slice(1)}`}
              MenuProps={MenuProps}
              IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
            >
              {dropdownOptions[key].map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={formValues[key] === name}
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': { color: zeplinColor.BasicBlack },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        {(['sport', 'position'] as const).map((key) => (
          <FormControl key={key} sx={{ flex: 1 }}>
            <InputLabel
              size={isXs ? 'small' : 'medium'}
              sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
            >
              {key.charAt(0).toUpperCase() + key.slice(1)}
            </InputLabel>
            <Select
              size={isXs ? 'small' : 'medium'}
              value={formValues[key] || ''}
              onChange={handleSelectChange(key)}
              input={
                <OutlinedInput
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: zeplinColor.Neutral9,
                    },
                  }}
                  label={key.charAt(0).toUpperCase() + key.slice(1)}
                />
              }
              renderValue={(s) => `${s.slice(0, 1).toLocaleUpperCase()}${s.slice(1)}`}
              MenuProps={MenuProps}
              IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
            >
              {dropdownOptions[key].map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={formValues[key] === name}
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': { color: zeplinColor.BasicBlack },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
          >
            Class
          </InputLabel>
          <Select
            size={isXs ? 'small' : 'medium'}
            value={formValues.class || ''}
            onChange={handleSelectChange('class')}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Class'
              />
            }
            renderValue={(s) => `${s.slice(0, 1).toLocaleUpperCase()}${s.slice(1)}`}
            MenuProps={MenuProps}
            IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
          >
            {dropdownOptions.class.map((name) => (
              <MenuItem key={name} value={name}>
                <Radio
                  checked={formValues.class === name}
                  sx={{
                    color: zeplinColor.BasicBlack,
                    '&.Mui-checked': { color: zeplinColor.BasicBlack },
                  }}
                />
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Recruiting Information */}
      <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
        Recruiting Information
      </Typography>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
          >
            Draft Projection
          </InputLabel>
          <Select
            size={isXs ? 'small' : 'medium'}
            value={formValues.draftProjection || ''}
            onChange={handleSelectChange('draftProjection')}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Draft Projection'
              />
            }
            renderValue={(s) => `${s.slice(0, 1).toLocaleUpperCase()}${s.slice(1)}`}
            MenuProps={MenuProps}
            IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
          >
            {dropdownOptions.draftProjection.map((name) => (
              <MenuItem key={name} value={name}>
                <Radio
                  checked={formValues.draftProjection === name}
                  sx={{
                    color: zeplinColor.BasicBlack,
                    '&.Mui-checked': { color: zeplinColor.BasicBlack },
                  }}
                />
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <CustomTextField
          style={{ flex: 1 }}
          name='Decision Maker'
          text={formValues.decisionMaker || ''}
          form
          label='Decision Maker'
          type='text'
          onChange={handleValueChange('decisionMaker')}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        <CustomTextField
          style={{ flex: 1 }}
          name='PFF Overall Grade'
          text={formValues.pffOverallGrade || ''}
          form
          label='PFF Overall Grade'
          type='text'
          onChange={handleValueChange('pffOverallGrade')}
        />
        <CustomTextField
          style={{ flex: 1 }}
          name='Agency'
          text={formValues.agency || ''}
          form
          label='Agency'
          type='text'
          onChange={handleValueChange('agency')}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
          >
            247 Sports Rating
          </InputLabel>
          <Select
            size={isXs ? 'small' : 'medium'}
            value={formValues.sports247Rating || ''}
            onChange={handleSelectChange('sports247Rating')}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='247 Sports Rating'
              />
            }
            renderValue={(s) => `${s.slice(0, 1).toLocaleUpperCase()}${s.slice(1)}`}
            MenuProps={MenuProps}
            IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
          >
            {dropdownOptions.sports247Rating.map((name) => (
              <MenuItem key={name} value={name}>
                <Radio
                  checked={formValues.sports247Rating === name}
                  sx={{
                    color: zeplinColor.BasicBlack,
                    '&.Mui-checked': { color: zeplinColor.BasicBlack },
                  }}
                />
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <CustomTextField
          style={{ flex: 1 }}
          name='Manager'
          text={formValues.manager || ''}
          form
          label='Manager'
          type='text'
          onChange={handleValueChange('manager')}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
          >
            Espn Rank
          </InputLabel>
          <Select
            size={isXs ? 'small' : 'medium'}
            value={formValues.espnRank || ''}
            onChange={handleSelectChange('espnRank')}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Espn Rank'
              />
            }
            renderValue={(s) => `${s.slice(0, 1).toLocaleUpperCase()}${s.slice(1)}`}
            MenuProps={MenuProps}
            IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
          >
            {dropdownOptions.espnRanking.map((name) => (
              <MenuItem key={name} value={name}>
                <Radio
                  checked={formValues.espnRank === name}
                  sx={{
                    color: zeplinColor.BasicBlack,
                    '&.Mui-checked': { color: zeplinColor.BasicBlack },
                  }}
                />
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <CustomTextField
          style={{ flex: 1 }}
          name='Referred By'
          text={formValues.referredBy || ''}
          form
          label='Referred By'
          type='text'
          onChange={handleValueChange('referredBy')}
        />
      </Box>

      {/* Contact Information */}
      <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
        Contact Information
      </Typography>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        <CustomTextField
          style={{ flex: 1 }}
          name='Mobile'
          text={formValues.mobile || ''}
          form
          label='Mobile'
          type='text'
          isPhoneMask
          onChange={handleValueChange('mobile')}
        />
        <CustomTextField
          style={{ flex: 1 }}
          name='Email'
          text={formValues.email || ''}
          form
          label='Email'
          type='text'
          onChange={handleValueChange('email')}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        <Box sx={{ flex: 1 }}>
          <CustomAutocomplete
            loading={loadingAddress}
            required={false}
            options={addressOptions || []}
            getOptionLabel={(option: any) => option?.Name ?? ''}
            label='Search Address'
            handleDeleteItems={() => {}}
            selectedItems={selectedItems}
            handleSelection={handleAddressSelection}
            value={selectedItems.address[0]}
            handleFilter={handleAddressSearch}
            placeholder='Search address'
            currentLength={addressOptions.length}
            totalLength={addressOptions.length}
            next={() => {}}
            multiple={false}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        <CustomTextField
          style={{ flex: 1 }}
          name='Street'
          text={formValues.street || ''}
          form
          label='Street'
          type='text'
          onChange={handleValueChange('street')}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        <CustomTextField
          style={{ flex: 1 }}
          name='City'
          text={formValues.city || ''}
          form
          label='City'
          type='text'
          onChange={handleValueChange('city')}
        />
        <CustomTextField
          style={{ flex: 1 }}
          name='State/Province'
          text={formValues.state || ''}
          form
          label='State/Province'
          type='text'
          onChange={handleValueChange('state')}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        <CustomTextField
          style={{ flex: 1 }}
          name='Zip/Postal Code'
          text={formValues.zip || ''}
          form
          label='Zip/Postal Code'
          type='text'
          onChange={handleValueChange('zip')}
        />
        <CustomTextField
          style={{ flex: 1 }}
          name='Country'
          text={formValues.country || ''}
          form
          label='Country'
          type='text'
          onChange={handleValueChange('country')}
        />
      </Box>

      {/* Social */}
      <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
        Social
      </Typography>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        <CustomTextField
          style={{ flex: 1 }}
          name='Instagram'
          text={formValues.instagram || ''}
          form
          label='Instagram'
          type='text'
          onChange={handleValueChange('instagram')}
        />
        <CustomTextField
          style={{ flex: 1 }}
          name='Twitter'
          text={formValues.twitter || ''}
          form
          label='Twitter'
          type='text'
          onChange={handleValueChange('twitter')}
        />
      </Box>

      {/* Attachments */}

      {attachmentFiles.length > 0 ? (
        <LeadAttachments
          files={attachmentFiles}
          onFileSelect={(files) => handleAttachmentDrop(files)}
          onFileDelete={onAttachmentDelete}
          leadId={leadId}
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
            Attachments
          </Typography>
          <CustomDragnDrop onDrop={(files) => handleAttachmentDrop(files)} />
        </Box>
      )}
    </Box>
  );
};

export default LeadDetailsForm;
